import { Component, ViewEncapsulation, ViewChild, ViewContainerRef, OnDestroy, ComponentRef, AfterViewInit, Input, OnInit, inject } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { takeUntil, take, map } from 'rxjs/operators';
import { appSettings } from '@nha/config';
import { CURRENT_APPLICATION, IPortalLink, PortalLinkService } from '@nha/api/core';
import { IApplicationComponentResolver } from './interfaces/iapplication-component-resolver.interface';
import { ApplicationComponentResolverService } from './services';
import { ComponentLoaderService } from '@nha/services';
import { BusinessSystem, EnvironmentNames } from '@nha/enums';
import { NHAUserProfileMenuComponent } from '../user-profile-menu/user-profile-menu.component';
import { NHAAppLauncherComponent } from '../app-launcher/app-launcher.component';
import { NHANotificationMenuComponent } from '../notification-menu/notification-menu.component';
import { MatChipsModule } from '@angular/material/chips';
import { RouterLink } from '@angular/router';
import { NHATestDirective } from '../../directives/test/test.directive';
import { MaintenanceNotificationComponent } from '../maintenence-notification/maintenance-notification.component';
import { CommonModule } from '@angular/common';
import { IfDesktopDirective } from '../../directives/responsive/if-desktop/if-desktop.directive';

@Component({
	selector: 'nha-application-container',
	templateUrl: './application-container.component.html',
	styleUrls: ['./application-container.component.scss'],
	encapsulation: ViewEncapsulation.None,
	imports: [
		CommonModule,
		IfDesktopDirective,
		MaintenanceNotificationComponent,
		NHATestDirective,
		RouterLink,
		MatChipsModule,
		NHANotificationMenuComponent,
		NHAAppLauncherComponent,
		NHAUserProfileMenuComponent
	]
})
export class NHAApplicationContainerComponent implements OnInit, AfterViewInit, OnDestroy {
	private _currentApplication$ = inject(CURRENT_APPLICATION);
	private _applicationComponentResolverService = inject(ApplicationComponentResolverService);
	private _componentLoaderService = inject(ComponentLoaderService);
	private _portalLinkService = inject(PortalLinkService);

	private _componentDestroyed$: Subject<boolean> = new Subject<boolean>();

	@ViewChild('applicationComponent', { read: ViewContainerRef })
	private _applicationComponentTarget: ViewContainerRef;
	@ViewChild('applicationMenuComponent', { read: ViewContainerRef })
	private _applicationMenuTarget: ViewContainerRef;

	private _componentRef: ComponentRef<Component>;
	private _componentMenuRef: ComponentRef<Component>;

	@Input() applicationSettingsKey: string;
	@Input() customApplicationName: string;
	@Input() baseRoute: string = '/';
	// Global notifications are rolled out app by app; each app opts in explicitly.
	@Input() showNotificationMenu: boolean = false;
	@Input() notificationBusinessSystemID: number = BusinessSystem.MyNHA;

	// supported environments - add corresponding classes/colors in .scss file
	// never add 'prod' to this list

	public showHeaderToken: boolean = ['tr'].includes(appSettings.env);
	public currentApplication$: Observable<IPortalLink> = this._currentApplication$.asObservable();
	public currentApplicationName$: Observable<string> = this.currentApplication$.pipe(map((ca) => this.customApplicationName || ca?.portalLinkName));
	public env: string = appSettings.env;
	public environmentName: string = EnvironmentNames[this.env.toUpperCase()];

	ngOnInit(): void {
		/** use appSettings to get current application */
		this._portalLinkService
			.getForKey(this.applicationSettingsKey)
			.pipe(take(1))
			.subscribe((portalLink) => this._currentApplication$.next(portalLink));
	}

	ngAfterViewInit(): void {
		this._componentLoaderService.addContainer('application-container-component-ref', this._applicationComponentTarget);
		this._componentLoaderService.addContainer('application-container-menu-component-ref', this._applicationMenuTarget);

		this._applicationComponentResolverService.resolver$.pipe(takeUntil(this._componentDestroyed$)).subscribe((resolver) => {
			if (resolver !== null) {
				if (resolver.menu) {
					this.updateApplicationMenuComponent(resolver);
				} else {
					this.updateApplicationComponent(resolver);
				}
			}
		});

		this._applicationComponentResolverService.remover$.pipe(takeUntil(this._componentDestroyed$)).subscribe((resolver) => {
			if (resolver !== null) {
				if (resolver.menu) {
					this.clearApplicationMenuComponent();
				} else {
					this.clearApplicationComponent();
				}
			}
		});
	}

	ngOnDestroy(): void {
		this._componentDestroyed$.next(true);
		this._componentDestroyed$.complete();
		this.clearApplicationComponent();
		this.clearApplicationMenuComponent();
	}

	private updateApplicationComponent(resolver: IApplicationComponentResolver): void {
		this.clearApplicationComponent();
		this._componentRef = this._applicationComponentTarget.createComponent(resolver.component);

		if (resolver.data) {
			Object.keys(resolver.data).forEach((key) => {
				this._componentRef.instance[key] = resolver.data[key];
			});
		}
	}

	private clearApplicationComponent(): void {
		if (this._applicationComponentTarget) {
			this._applicationComponentTarget.clear();
		}
		if (this._componentRef) {
			this._componentRef.destroy();
		}
	}

	private updateApplicationMenuComponent(resolver: IApplicationComponentResolver): void {
		this.clearApplicationMenuComponent();
		this._componentMenuRef = this._applicationMenuTarget.createComponent(resolver.component);

		if (resolver.data) {
			Object.keys(resolver.data).forEach((key) => {
				this._componentMenuRef.instance[key] = resolver.data[key];
			});
		}
	}

	private clearApplicationMenuComponent(): void {
		if (this._applicationMenuTarget) {
			this._applicationMenuTarget.clear();
		}
		if (this._componentMenuRef) {
			this._componentMenuRef.destroy();
		}
	}
}
